/**
 * AnalisisPage (rc98) — módulo Análisis: resúmenes ejecutivos.
 *
 * Sección 1: Resumen ejecutivo — mes en curso vs anterior (neto de
 *   contratos, recaudado vs mismo corte, cartera vencida hoy, parque).
 * Sección 2: Crecimiento — altas vs bajas por mes (bars), activos al
 *   cierre (línea), y tabla mensual completa para contabilidad.
 *
 * rc99 traerá: Cobranza (facturado vs cobrado, morosos/mes, aging) e
 * Ingresos (por origen, ARPU, MRR) + exports CSV.
 *
 * Filtro por empresa: consolidado por default; el filtro viaja como
 * ?empresa_id al backend (la empresa vive en el cliente).
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/icons/Icon";
import { formatMXN, cn } from "@/lib/utils";
import { useEmpresasMap } from "@/features/empresas/useEmpresasMap";

import {
  useResumenEjecutivo, useCrecimiento, type CrecimientoMes,
} from "./api";
import { DetalleMesDialog } from "./DetalleMesDialog";
import { MorososDialog } from "./MorososDialog";

const MESES_LABEL = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

/** "2026-08" → "ago 26" */
function mesLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return `${MESES_LABEL[(m ?? 1) - 1]} ${String(y).slice(2)}`;
}

type Rango = 6 | 12 | 24;

export function AnalisisPage() {
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [rango, setRango] = useState<Rango>(12);

  const { empresas } = useEmpresasMap();
  const { data: resumen } = useResumenEjecutivo(empresaId);
  const { data: crecimiento } = useCrecimiento(rango, empresaId);

  const serie = crecimiento?.serie ?? [];

  // rc99 — drill-downs: mes seleccionado (modal altas/bajas) + morosos
  const [detalleMes, setDetalleMes] = useState<string | null>(null);
  const [morososOpen, setMorososOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Icon name="activity" size={16} className="text-electric" /> Análisis
          </h2>
          <p className="text-xs text-muted-foreground">
            Resúmenes ejecutivos del negocio. Serie por contratos (altas ={" "}
            fecha de activación real).
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Empresa
            </div>
            <Select
              value={empresaId === null ? "" : String(empresaId)}
              onChange={(e) =>
                setEmpresaId(e.target.value === "" ? null : Number(e.target.value))
              }
              className="text-xs min-w-[200px]"
            >
              <option value="">Todas (consolidado)</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}{!e.activa ? " (inactiva)" : ""}
                </option>
              ))}
            </Select>
          </div>
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden">
            {([6, 12, 24] as Rango[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRango(r)}
                className={cn(
                  "font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 transition-colors",
                  r === rango
                    ? "bg-electric text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/40",
                )}
              >
                {r}M
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Resumen ejecutivo ─────────────────────────────────────────── */}
      {!resumen ? (
        <div className="py-8 text-center text-xs text-muted-foreground">
          <Loader2 size={18} className="mx-auto mb-2 animate-spin" />
          cargando resumen…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {/* #1 NETO DEL MES — highlight */}
          <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Neto del mes · contratos
              </div>
              <Icon name="activity" size={12} className="text-electric" />
            </div>
            <div
              className={cn(
                "font-mono text-2xl font-semibold tabular-nums tracking-tight",
                resumen.mes_actual.neto > 0
                  ? "text-success"
                  : resumen.mes_actual.neto < 0
                    ? "text-danger"
                    : "text-electric",
              )}
            >
              {resumen.mes_actual.neto > 0 ? "+" : ""}{resumen.mes_actual.neto}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">
              {resumen.mes_actual.altas} altas · {resumen.mes_actual.bajas} bajas
            </div>
            <div className="font-mono text-[10px] text-muted-foreground mt-2">
              mes anterior: {resumen.mes_anterior.neto > 0 ? "+" : ""}
              {resumen.mes_anterior.neto} ({resumen.mes_anterior.altas}a/
              {resumen.mes_anterior.bajas}b)
            </div>
          </Card>

          {/* #2 RECAUDADO DEL MES */}
          <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Recaudado · mes en curso
              </div>
              <Icon name="wallet" size={12} className="text-electric" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-electric">
              {formatMXN(Number(resumen.mes_actual.recaudado))}
            </div>
            <div className="font-mono text-[11px] mt-1 flex items-center gap-1">
              <span className={resumen.delta_recaudado_pct >= 0 ? "text-success" : "text-danger"}>
                {resumen.delta_recaudado_pct >= 0 ? "↗ +" : "↘ "}
                {resumen.delta_recaudado_pct.toFixed(1)}%
              </span>
              <span className="text-muted-foreground">vs mes ant. mismo corte</span>
            </div>
            <div className="font-mono text-[10px] text-muted-foreground mt-2">
              mes ant. completo: {formatMXN(Number(resumen.mes_anterior.recaudado_total))}
            </div>
          </Card>

          {/* #3 CARTERA VENCIDA HOY — rc99: click abre lista de morosos */}
          <Card
            className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md cursor-pointer"
            onClick={() => setMorososOpen(true)}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Cartera vencida hoy
              </div>
              <Icon name="alert" size={12} className="text-danger" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-danger">
              {formatMXN(Number(resumen.cartera_vencida.monto))}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">
              {resumen.cartera_vencida.facturas} mensualidades vencidas
            </div>
            <div className="font-mono text-[10px] mt-2 flex items-center justify-between">
              <span className="text-warn">{resumen.cartera_vencida.clientes_morosos} clientes morosos</span>
              <span className="text-muted-foreground uppercase tracking-wider text-[9px]">ver quiénes →</span>
            </div>
          </Card>

          {/* #4 PARQUE DE CONTRATOS */}
          <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Parque de contratos
              </div>
              <Icon name="users" size={12} className="text-muted-foreground" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight">
              {resumen.contratos.activos}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">activos hoy</div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono">
              <span className="flex items-center gap-1 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-warn"></span>
                susp {resumen.contratos.suspendidos}
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-danger"></span>
                error {resumen.contratos.error}
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50"></span>
                pend {resumen.contratos.pendientes}
              </span>
            </div>
          </Card>
        </div>
      )}

      {/* ── Crecimiento ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-12">
        {/* Altas vs bajas */}
        <Card className="rounded-xl p-4 shadow-sm lg:col-span-8">
          <h3 className="flex items-center gap-2 text-sm font-semibold mb-1">
            <Icon name="activity" size={14} className="text-electric" />
            Altas vs bajas · últimos {rango} meses
          </h3>
          <div className="flex items-center gap-4 mb-3 font-mono text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[2px] bg-electric"></span> altas
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[2px] bg-danger opacity-80"></span> bajas
            </span>
          </div>
          {serie.length > 0 ? (
            <GroupedBarsSVG serie={serie} onSelectMes={setDetalleMes} />
          ) : (
            <div className="py-12 text-center text-xs text-muted-foreground">
              <Loader2 size={18} className="mx-auto mb-2 animate-spin" />
              cargando serie…
            </div>
          )}
          {/* Totales del periodo */}
          {serie.length > 0 && (
            <div className="flex items-baseline gap-6 mt-3 border-t border-border pt-3">
              <PeriodStat
                label="Altas periodo"
                value={String(serie.reduce((s, m) => s + m.altas, 0))}
                tone="electric"
              />
              <PeriodStat
                label="Bajas periodo"
                value={String(serie.reduce((s, m) => s + m.bajas, 0))}
                tone="danger"
              />
              <PeriodStat
                label="Neto periodo"
                value={(() => {
                  const n = serie.reduce((s, m) => s + m.neto, 0);
                  return `${n > 0 ? "+" : ""}${n}`;
                })()}
                tone={serie.reduce((s, m) => s + m.neto, 0) >= 0 ? "success" : "danger"}
              />
              <PeriodStat
                label="Churn prom."
                value={`${(
                  serie.reduce((s, m) => s + m.churn_pct, 0) / serie.length
                ).toFixed(2)}%`}
              />
            </div>
          )}
          {/* rc99 — transparencia: migrados sin fecha de activación */}
          {(crecimiento?.sin_fecha_activacion ?? 0) > 0 && (
            <p className="font-mono text-[10px] text-muted-foreground mt-2">
              ⓘ {crecimiento?.sin_fecha_activacion} contratos migrados sin fecha
              de activación cuentan en el parque desde el inicio de la serie
              (no como altas de ningún mes).
            </p>
          )}
        </Card>

        {/* Parque al cierre (activos + suspendidos = cartera viva) */}
        <Card className="rounded-xl p-4 shadow-sm lg:col-span-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold mb-0.5">
            <Icon name="users" size={14} className="text-electric" />
            Parque al cierre
          </h3>
          <p className="font-mono text-[10px] text-muted-foreground mb-3">
            activos + suspendidos (cartera viva, sin cancelados)
          </p>
          {serie.length > 0 ? (
            <>
              <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight">
                {serie[serie.length - 1].activos_cierre}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground mb-3">
                al corte de {mesLabel(serie[serie.length - 1].mes)} (en curso)
              </div>
              <LineSVG
                points={serie.map((m) => m.activos_cierre)}
                labels={serie.map((m) => mesLabel(m.mes))}
              />
              <div className="font-mono text-[10px] text-muted-foreground mt-2">
                hace {rango} meses: {serie[0].activos_cierre}
                {" · "}
                {(() => {
                  const d = serie[serie.length - 1].activos_cierre - serie[0].activos_cierre;
                  return (
                    <span className={d >= 0 ? "text-success" : "text-danger"}>
                      {d >= 0 ? "+" : ""}{d} en el periodo
                    </span>
                  );
                })()}
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-xs text-muted-foreground">…</div>
          )}
        </Card>
      </div>

      {/* Tabla mensual (contabilidad) */}
      <Card className="rounded-xl p-4 shadow-sm">
        <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
          <Icon name="file-text" size={14} className="text-electric" />
          Detalle mensual
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-1.5 pr-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Mes</th>
                <th className="py-1.5 pr-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Altas</th>
                <th className="py-1.5 pr-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Bajas</th>
                <th className="py-1.5 pr-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Neto</th>
                <th className="py-1.5 pr-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Clientes nuevos</th>
                <th className="py-1.5 pr-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Activos cierre</th>
                <th className="py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Churn</th>
              </tr>
            </thead>
            <tbody>
              {[...serie].reverse().map((m) => (
                <tr
                  key={m.mes}
                  className="border-b border-border/50 last:border-0 hover:bg-muted/20 cursor-pointer"
                  title="Click para ver quiénes fueron las altas y bajas"
                  onClick={() => setDetalleMes(m.mes)}
                >
                  <td className="py-1.5 pr-4 font-mono">
                    {mesLabel(m.mes)}
                    {m.mes_en_curso && (
                      <Badge variant="warning" className="ml-2 text-[9px]">parcial</Badge>
                    )}
                  </td>
                  <td className="py-1.5 pr-4 font-mono tabular-nums text-right">{m.altas}</td>
                  <td className="py-1.5 pr-4 font-mono tabular-nums text-right">{m.bajas}</td>
                  <td
                    className={cn(
                      "py-1.5 pr-4 font-mono tabular-nums text-right",
                      m.neto > 0 ? "text-success" : m.neto < 0 ? "text-danger" : "text-muted-foreground",
                    )}
                  >
                    {m.neto > 0 ? "+" : ""}{m.neto}
                  </td>
                  <td className="py-1.5 pr-4 font-mono tabular-nums text-right">{m.clientes_nuevos}</td>
                  <td className="py-1.5 pr-4 font-mono tabular-nums text-right">{m.activos_cierre}</td>
                  <td className="py-1.5 font-mono tabular-nums text-right text-muted-foreground">
                    {m.churn_pct.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-mono text-[10px] text-muted-foreground mt-2">
          Click en un mes para ver el detalle de altas y bajas.
        </p>
      </Card>

      {/* rc99 — modales de drill-down */}
      <DetalleMesDialog
        mes={detalleMes}
        mesLabel={detalleMes ? mesLabel(detalleMes) : ""}
        empresaId={empresaId}
        onClose={() => setDetalleMes(null)}
      />
      <MorososDialog
        open={morososOpen}
        empresaId={empresaId}
        onClose={() => setMorososOpen(false)}
      />
    </div>
  );
}

// ─── Componentes internos ────────────────────────────────────────────────
function PeriodStat({
  label, value, tone,
}: { label: string; value: string; tone?: "electric" | "success" | "danger" }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </div>
      <div
        className={cn(
          "font-mono text-lg font-semibold tabular-nums tracking-tight",
          tone === "electric" && "text-electric",
          tone === "success" && "text-success",
          tone === "danger" && "text-danger",
        )}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Bars agrupadas altas (electric) vs bajas (danger) por mes.
 * SVG puro sin librerías — mismo patrón que BarChartSVG del dashboard (rc63).
 * rc99: tooltip REAL al hover (columna resaltada + caja flotante con altas/
 * bajas/neto del mes) y click en cualquier mes abre el drill-down.
 */
function GroupedBarsSVG({
  serie, onSelectMes,
}: { serie: CrecimientoMes[]; onSelectMes: (mes: string) => void }) {
  const w = 700;
  const h = 150;
  const padB = 18;
  const maxVal = Math.max(...serie.map((m) => Math.max(m.altas, m.bajas)), 1);
  const groupW = w / serie.length;
  const barW = Math.max(3, Math.min(16, groupW * 0.32));
  const gapInner = 2;

  const [hover, setHover] = useState<number | null>(null);
  const hovered = hover !== null ? serie[hover] : null;
  // Posición del tooltip en % del ancho (clamp para que no se salga)
  const tipLeftPct = hover !== null
    ? Math.min(86, Math.max(2, ((hover + 0.5) / serie.length) * 100))
    : 0;

  return (
    <div className="relative">
      {hovered && (
        <div
          className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 rounded-md border border-border bg-card px-2.5 py-1.5 shadow-md"
          style={{ left: `${tipLeftPct}%` }}
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {mesLabel(hovered.mes)}{hovered.mes_en_curso ? " · parcial" : ""}
          </div>
          <div className="font-mono text-[11px] tabular-nums whitespace-nowrap">
            <span className="text-electric">{hovered.altas} altas</span>
            {" · "}
            <span className="text-danger">{hovered.bajas} bajas</span>
            {" · "}
            <span className={hovered.neto >= 0 ? "text-success" : "text-danger"}>
              {hovered.neto > 0 ? "+" : ""}{hovered.neto} neto
            </span>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground tabular-nums">
            parque al cierre: {hovered.activos_cierre} · churn {hovered.churn_pct.toFixed(2)}%
          </div>
        </div>
      )}
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-36 cursor-pointer"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        {serie.map((m, i) => {
          const x0 = i * groupW;
          const cx = x0 + groupW / 2;
          const altasH = m.altas > 0 ? Math.max(2, (m.altas / maxVal) * (h - padB - 6)) : 0;
          const bajasH = m.bajas > 0 ? Math.max(2, (m.bajas / maxVal) * (h - padB - 6)) : 0;
          return (
            <g
              key={m.mes}
              opacity={m.mes_en_curso ? 0.65 : 1}
              onMouseEnter={() => setHover(i)}
              onClick={() => onSelectMes(m.mes)}
            >
              {/* Columna de captura de hover (invisible, todo el alto) */}
              <rect
                x={x0} y={0} width={groupW} height={h}
                fill={hover === i ? "hsl(var(--electric))" : "transparent"}
                opacity={hover === i ? 0.06 : 0}
              />
              <rect
                x={cx - barW - gapInner / 2}
                y={h - padB - altasH}
                width={barW}
                height={altasH}
                rx={1}
                fill="hsl(var(--electric))"
                opacity={m.altas > 0 ? 0.95 : 0.15}
              />
              <rect
                x={cx + gapInner / 2}
                y={h - padB - bajasH}
                width={barW}
                height={bajasH}
                rx={1}
                fill="hsl(var(--danger))"
                opacity={m.bajas > 0 ? 0.8 : 0.15}
              />
            </g>
          );
        })}
        <line x1={0} y1={h - padB} x2={w} y2={h - padB} className="stroke-border" strokeWidth="0.5" />
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-muted-foreground tabular-nums mt-1 px-1">
        <span>{mesLabel(serie[0].mes)}</span>
        <span>{mesLabel(serie[Math.floor(serie.length / 2)].mes)}</span>
        <span>{mesLabel(serie[serie.length - 1].mes)}</span>
      </div>
    </div>
  );
}

/** Línea del parque al cierre — SVG puro, área tenue debajo.
 *  rc99: tooltip real al hover con el valor del mes. */
function LineSVG({ points, labels }: { points: number[]; labels: string[] }) {
  const w = 300;
  const h = 90;
  const padB = 4;
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(max - min, 1);
  const xy = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - padB - ((p - min) / range) * (h - padB - 8);
    return [x, y] as const;
  });
  const path = xy.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${w},${h} L0,${h} Z`;
  const tipLeftPct = hover !== null
    ? Math.min(80, Math.max(4, (hover / (points.length - 1)) * 100))
    : 0;

  return (
    <div className="relative">
      {hover !== null && (
        <div
          className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 rounded-md border border-border bg-card px-2 py-1 shadow-md whitespace-nowrap"
          style={{ left: `${tipLeftPct}%` }}
        >
          <span className="font-mono text-[10px] text-muted-foreground">{labels[hover]}: </span>
          <span className="font-mono text-[11px] tabular-nums text-electric">{points[hover]}</span>
        </div>
      )}
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-24"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        <path d={area} fill="hsl(var(--electric))" opacity={0.08} />
        <path d={path} fill="none" stroke="hsl(var(--electric))" strokeWidth={1.75} />
        {/* Columnas invisibles de captura de hover */}
        {points.map((_, i) => (
          <rect
            key={`hz-${i}`}
            x={(i - 0.5) * (w / (points.length - 1))}
            y={0}
            width={w / (points.length - 1)}
            height={h}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
        {xy.map(([x, y], i) => (
          <circle
            key={i} cx={x} cy={y}
            r={hover === i ? 2.6 : 1.6}
            fill="hsl(var(--electric))"
          />
        ))}
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-muted-foreground tabular-nums mt-1">
        <span>{labels[0]}</span>
        <span>{labels[labels.length - 1]}</span>
      </div>
    </div>
  );
}
